import Cookies from 'js-cookie';
import articleConfig from '~/config/article';
import { ENV } from '~/config/article/env';
import { createMarker, markers, measures, measureTimeBetweenMarks } from '~/debug';
import { detectBrowser, detectDevice, detectOS } from '~/utils/client-info';
import { generateGUID } from '~/utils/general';
import { safeLocalStorageGet, safeLocalStorageSetDelete, safeLocalStorageSetForDuration } from '~/utils/localStorage';
import TokenHandler from './TokenHandler';
import { isInCrossOriginFrame, isInIframe } from '~/utils/dom';

const windowContext = isInIframe() && !isInCrossOriginFrame() && window.top ? window.top : window;

interface IVuukleGlobalObjectData {
  countryName?: string;
  country_code?: string;
  region: string | null;
  browser: string;
  os: string;
  device: string;
}

/**
 * Session class contains methods to receive user UUIDs or create them if needed
 */
class Session {
  /** Cookie use is allowed/disabled */
  private _canUseCookies: boolean = ENV.NODE_ENV === 'test';
  public sameSiteCookieOptions: Partial<Cookies.CookieAttributes> = { expires: 30, sameSite: 'lax' };
  /** Country of the user */
  public userCountry: string = '';
  /** If we know the user country or not */
  public userCountryDetermined: boolean = false;
  /** On auth, comments widget sends a communication message to update user API key through the userAPIKey setter */
  private userID: string = '';
  /** Storage update functions (cookies.set or localStorage.setItem) that are saved for later execution until cookies are accepted */
  private storageUpdateCallbacks: Set<() => void> = new Set();
  // User hardware and software information
  public browser: string = 'unknown';
  public os: string = 'unknown';
  public device: string = 'unknown';
  public isMobileUser: boolean = windowContext.innerWidth <= 1024;
  /** Callbacks that listen to cookie use change */
  private cookieChangeListeners: Map<string, (value: boolean) => any> = new Map();
  /** Callbacks that fire when geo data is received */
  private onGeoDataReceived: Map<string, (value: any) => any> = new Map();
  /** If the user agent is a bot or not */
  public isBot: boolean =
    /(bot|check|cloud|crawler|download|monitor|preview|scan|spider|google|qwantify|yahoo|facebookexternalhit|flipboard|tumblr|vkshare|whatsapp|curl|perl|python|wget|heritrix|ia_archiver)/i.test(
      navigator.userAgent
    );
  /** Unix timestamp of time when platform loads */
  public startTimestamp = Math.floor(Date.now() / 1000);
  /** Is it safe to run ads on the page, required for BQ Analytics */
  private _isArticleBrandSafe: boolean | null = null;
  public isArticleBrandSafeKnown: boolean = false;

  constructor() {
    // In some cases platform.js might get inserted more than once
    // Which is why we're preventing re-assignment of this function by duplicated copies of platform.js
    if (!window.VUUKLE_PLATFORM) {
      window.addVuukleCookieConsentListener = this.addCookieConsentListener.bind(this);
    }
  }

  /** Can platform and widgets set cookies or not */
  public get canUseCookies(): boolean {
    return this._canUseCookies;
  }

  public get isArticleBrandSafe() {
    return this._isArticleBrandSafe;
  }

  public set isArticleBrandSafe(value: boolean | null) {
    this._isArticleBrandSafe = value;
    this.isArticleBrandSafeKnown = true;
  }

  /** Set the ability to use cookies to a new variable, execute callbacks if any */
  public set canUseCookies(newValue: boolean) {
    if (this._canUseCookies !== newValue) {
      this._canUseCookies = newValue;
      import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
        consentManager.default.cookieConsentKnown = true;
      });
      this.executeCallbacks();
      this.cookieChangeListeners.forEach((listener) => listener(newValue));
      /*
        Quiz pixel will likely leave behind a temporary cookie consent listener if the addVuukleCookieConsentListener 
        is not available for it due to platform loading later than the pixel.
        In that case, execute the listener code and add it to the permanent list of cookie consent listeners.
       */
      if (Array.isArray(window.tempVuukleCookieConsentListeners)) {
        window.tempVuukleCookieConsentListeners.forEach((value) => {
          if (typeof value === 'function') {
            value(newValue);
            const callbackFunction = value;
            this.addCookieConsentListener(`temp-listener-${this.cookieChangeListeners.size}`, callbackFunction);
          } else {
            const [listener, storageKey] = value;
            import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
              consentManager.default.executeWhenStorageValueCanBeSet(storageKey, () => listener(newValue));
            });
            const callbackFunction = listener;
            this.addCookieConsentListener(
              `temp-listener-${this.cookieChangeListeners.size}`,
              callbackFunction,
              storageKey
            );
          }
        });
        window.tempVuukleCookieConsentListeners = null;
      }
    }
  }

  public addCookieConsentListener(index: string, callback: (value: boolean) => any, storageKey: string = '') {
    import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
      if (consentManager.default.cookieConsentKnown) {
        if (!storageKey) {
          callback(this.canUseCookies);
        } else {
          consentManager.default.executeWhenStorageValueCanBeSet(storageKey, () => callback(this.canUseCookies));
        }
      } else {
        this.cookieChangeListeners.set(index, (value) => {
          if (!storageKey) {
            callback(value);
          } else {
            consentManager.default.executeWhenStorageValueCanBeSet(storageKey, () => callback(value));
          }
        });
      }
    });
  }

  /**
   * Add or call a geo listener right away
   * @param index
   * @param callback
   */
  public addGeoListener(index: string, callback: (value: any) => any) {
    if (this.userCountryDetermined) {
      callback({ country: this.userCountry });
    } else {
      this.onGeoDataReceived.set(index, callback);
    }
  }

  /**
   * Add a callback function that's called after the cookie consent is given
   * @param callback
   */
  public addStorageUpdateCallback(callback: (...args: unknown[]) => unknown) {
    this.storageUpdateCallbacks.add(callback);
  }

  /**
   * Remove a cookie change listener by index
   * @param index the index
   */
  public removeCookieListenerByIndex(index: string) {
    if (this.cookieChangeListeners.get('-' + index)) {
      this.cookieChangeListeners.delete('-' + index);
    }
  }

  /**
   * Remove a geo data receive event listener by index
   * @param index the index
   */
  public removeGeoListenersByIndex(index: string) {
    if (this.onGeoDataReceived.get('-' + index)) {
      this.onGeoDataReceived.delete('-' + index);
    }
  }

  /**
   * First it tries to get UUID from 'vsid' cookie and return it, if it's not available
   * it generates new UUID, saves in 'vsid' cookie and returns it.
   * Used by:
   * @see ServerLogger
   * @returns {string} session UUID
   */
  public get sessionID(): string {
    const ID = articleConfig.amp ? safeLocalStorageGet('vsid') : Cookies.get('vsid');
    if (!ID && this.canUseCookies) {
      const newID = generateGUID();
      if (ENV.NODE_ENV === 'test') {
        Cookies.set('vsid', newID, this.sameSiteCookieOptions);
      } else {
        import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
          consentManager.default.executeWhenStorageValueCanBeSet('vsid', () => {
            articleConfig.amp
              ? safeLocalStorageSetDelete('vsid', newID)
              : Cookies.set('vsid', newID, this.sameSiteCookieOptions);
          });
        });
      }
      return newID;
    }

    if (!ID && !this.canUseCookies) {
      const newID = generateGUID();
      this.addStorageUpdateCallback(() => {
        if (ENV.NODE_ENV === 'test') {
          Cookies.set('vsid', newID, this.sameSiteCookieOptions);
        } else {
          import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
            consentManager.default.executeWhenStorageValueCanBeSet('vsid', () => {
              articleConfig.amp
                ? safeLocalStorageSetDelete('vsid', newID)
                : Cookies.set('vsid', newID, this.sameSiteCookieOptions);
            });
          });
        }
      });
    }

    if (ID) {
      return ID;
    }

    return generateGUID();
  }

  /** Execute all the storage update callbacks if cookies are enabled */
  private executeCallbacks() {
    if (this.canUseCookies && this.storageUpdateCallbacks.size > 0) {
      this.storageUpdateCallbacks.forEach((callback) => callback());
      this.storageUpdateCallbacks.clear();
    }
  }

  /**
   * Tries to find authorized user API key if there's one received from Comments Widget, if it's not there then it's checking
   * if anonymous API key 'uid-s' is available and returns it, otherwise creates anonymous API key, saves in 'uid-s'
   * and returns.
   * Used by:
   * @see ServerLogger.ts
   * @see RealtimeAnalytics.ts
   * @returns {string} authorized user API key or anon user API key
   */
  public get userAPIKey(): string {
    // If there's a saved uid for us
    if (this.userID) {
      return this.userID;
    }
    const userId = safeLocalStorageGet('vuukle_user_id');
    if (userId) {
      return userId;
    }
    // If we created anon id earlier to reuse
    const oldAnonID = articleConfig.amp ? safeLocalStorageGet('uid-s') : Cookies.get('uid-s');
    if (oldAnonID) {
      return '$' + oldAnonID.substring(1);
    }
    // Otherwise create it, save in cookies and return
    const anonID = `${generateGUID().substring(1)}`;
    if (this.canUseCookies) {
      if (ENV.NODE_ENV === 'test') {
        Cookies.set('uid-s', anonID, { expires: 365, sameSite: 'lax' });
      } else {
        import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
          consentManager.default.executeWhenStorageValueCanBeSet('uid-s', () => {
            articleConfig.amp
              ? safeLocalStorageSetDelete('uid-s', anonID)
              : Cookies.set('uid-s', anonID, { expires: 365, sameSite: 'lax' });
          });
        });
      }
    } else {
      this.addStorageUpdateCallback(() => {
        if (ENV.NODE_ENV === 'test') {
          Cookies.set('uid-s', anonID, { expires: 365, sameSite: 'lax' });
        } else {
          import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
            consentManager.default.executeWhenStorageValueCanBeSet('uid-s', () => {
              articleConfig.amp
                ? safeLocalStorageSetDelete('uid-s', anonID)
                : Cookies.set('uid-s', anonID, { expires: 365, sameSite: 'lax' });
            });
          });
        }
      });
    }
    return '$' + anonID.substring(1);
  }

  /**
   * Save the uid of the user in Session class
   * Used by Comments Widget to set and delete uid on auth/logout
   */
  public set userAPIKey(apiKey: string) {
    this.userID = apiKey;
  }

  /** Delete cookies we set in platform */
  public deleteCookies() {
    if (articleConfig.amp) {
      safeLocalStorageSetDelete('vsid', null);
      safeLocalStorageSetDelete('uid-s', null);
      safeLocalStorageSetDelete('vuukle_geo_region', null);
    } else {
      Cookies.remove('vsid', this.sameSiteCookieOptions);
      Cookies.remove('uid-s', { expires: 365, sameSite: 'Lax' });
      Cookies.remove('vuukle_geo_region', { expires: 7, sameSite: 'Lax' });
      Cookies.remove('vuukle_notification_subscription', { expires: 180, sameSite: 'Lax' });
      Cookies.remove('vuukle_notification_subscription_dismissed', { expires: 1, sameSite: 'Lax' });
    }
    TokenHandler.removeToken();
    TokenHandler.setAnonToken(null);
    safeLocalStorageSetDelete('hrefAfter', null);
    safeLocalStorageSetDelete('showedNotes', null);
    safeLocalStorageSetDelete(`vuukleconfhttps://cdn.vuukle.com/ads/${articleConfig.host}.json`, null);
    safeLocalStorageSetDelete(`vuukleconftimehttps://cdn.vuukle.com/ads/${articleConfig.host}.json`, null);
    safeLocalStorageSetDelete('_vuukleGeo', null);
    safeLocalStorageSetDelete('vuukle_quiz_answers', null);
    safeLocalStorageSetDelete('quizzly_surveys_response_groups', null);
    safeLocalStorageSetDelete('vuukle_user_id', null);
    safeLocalStorageSetDelete('vuukle_cookie_usage_agreed', null);
    safeLocalStorageSetDelete('vuukle_quiz_frequency_cap', null);
    safeLocalStorageSetDelete('vuukle_quiz_reported_questions', null);
  }

  /**
   * Provide a minimal global object so that prebid doesn't execute an API request for this data
   * @param data
   */
  private async provideUserInfoInGlobalObject(data: IVuukleGlobalObjectData) {
    let browser = data.browser;
    if (this.browser === 'unknown' || browser === 'unknown') {
      browser = await detectBrowser();
    }
    const geoData = {
      city: data.region,
      country_code: data.countryName || data.country_code,
      browser,
      device: data.device,
      os: data.os,
    };
    if (!window._vuukleGeo) {
      window._vuukleGeo = geoData;
    } else {
      window._vuukleGeo = {
        ...window._vuukleGeo,
        ...geoData,
      };
    }
    /** Fire callbacks with geo data */
    this.onGeoDataReceived.forEach((callback) => callback({ country: data.countryName || data.country_code }));
  }

  /** Cookies are going to be disabled in GDPR compliant countries */
  public async determineCookieUsability(countryCode: string | null, region: string | null) {
    createMarker(markers.geoRequestStart);
    measureTimeBetweenMarks(measures.platformStartToGeoStart, markers.platformStart, markers.geoRequestStart);
    const consentManager = await import(/* webpackChunkName: "cmg" */ './ConsentManager');
    const updateGeoData = (country: string) => {
      if (!consentManager.default.GDPRCountryCodes.includes(country)) {
        this.canUseCookies = true;
        consentManager.default.cookieConsentKnown = true;
      }
      this.userCountry = country;
    };

    const updateGeoCookie = (info: IVuukleGlobalObjectData) => {
      if (!articleConfig.disableGeoCookie) {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_geo_region', () => {
          articleConfig.amp
            ? safeLocalStorageSetForDuration('vuukle_geo_region', JSON.stringify(info), 7 * 24)
            : Cookies.set('vuukle_geo_region', JSON.stringify(info), {
                expires: 7,
                sameSite: 'lax',
              });
        });
      }
    };

    const cachedRegion = articleConfig.amp
      ? safeLocalStorageGet('vuukle_geo_region')
      : Cookies.get('vuukle_geo_region');

    const vuukleGeo = window._vuukleGeo;

    if (cachedRegion) {
      const parsedRegion = JSON.parse(cachedRegion);
      this.browser = parsedRegion.browser || 'unknown';
      this.os = parsedRegion.os || 'unknown';
      this.device = parsedRegion.device || 'unknown';
      this.userCountry =
        typeof countryCode === 'string' && countryCode !== parsedRegion.country_code
          ? countryCode
          : parsedRegion.country_code;
      this.provideUserInfoInGlobalObject({ ...parsedRegion, country_code: this.userCountry });
      createMarker(markers.geoRequestCache);
      measureTimeBetweenMarks(measures.platformStartToGeoCache, markers.platformStart, markers.geoRequestCache);
      updateGeoData(this.userCountry);
      if (this.userCountry !== parsedRegion.country_code) {
        updateGeoCookie({
          browser: this.browser,
          device: this.device,
          os: this.os,
          region,
          country_code: this.userCountry,
        });
      }
      this.userCountryDetermined = true;
    } else if (vuukleGeo) {
      createMarker(markers.geoRequestCache);
      measureTimeBetweenMarks(measures.platformStartToGeoCache, markers.platformStart, markers.geoRequestCache);
      updateGeoData(vuukleGeo.country_code);
      /** Fire callbacks with geo data */
      this.onGeoDataReceived.forEach((callback) => callback({ country: vuukleGeo.country_code }));
      this.userCountryDetermined = true;
      if (vuukleGeo.country_code !== countryCode && typeof countryCode === 'string') {
        updateGeoCookie({
          browser: vuukleGeo.browser,
          device: vuukleGeo.device,
          os: vuukleGeo.os,
          region: vuukleGeo.region || region,
          country_code: countryCode,
        });
      }
    } else if (countryCode) {
      const isGDPRCountry = consentManager.default.GDPRCountryCodes.includes(countryCode);
      const isCCPA = region === 'California';
      consentManager.default.isGDPR = isGDPRCountry;
      consentManager.default.isCCPA = isCCPA;
      this.userCountry = countryCode;
      this.canUseCookies = !isGDPRCountry && !isCCPA;
      this.device = await detectDevice();
      this.os = await detectOS();
      this.browser = await detectBrowser();
      const userInfoJSON = {
        country_code: countryCode,
        region,
        os: this.os,
        device: this.device,
        browser: this.browser,
      };
      // Assume cookie consent granted if not in GDPR/CCPA zone
      if (!isGDPRCountry && !isCCPA) {
        consentManager.default.cookieConsentKnown = true;
      } else {
        consentManager.default.checkForConsent();
      }
      updateGeoCookie(userInfoJSON);
      this.userCountryDetermined = true;
      this.provideUserInfoInGlobalObject(userInfoJSON);
    }
  }
}

export default new Session();
